import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./CitySelect.css";

// Searchable city picker. `value` / `onChange` use the city id as a string,
// same as the native <select> it replaces.
// variant "desktop" opens a dropdown under the field; "mobile" opens a bottom sheet.
function CitySelect({
    cities = [],
    value,
    onChange,
    placeholder = "Select city",
    variant = "desktop",
    icon,
    disabled = false,
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [highlight, setHighlight] = useState(0);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const isMobile = variant === "mobile";

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = q ? cities.filter((c) => (c.name || "").toLowerCase().includes(q)) : cities;
        // Names that start with the query rank above names that merely contain it
        return q ? [...list].sort((a, b) => a.name.toLowerCase().indexOf(q) - b.name.toLowerCase().indexOf(q)) : list;
    }, [cities, query]);

    const selected = cities.find((c) => String(c.id) === String(value));

    useEffect(() => {
        if (!open || isMobile) return undefined;
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open, isMobile]);

    useEffect(() => {
        if (!open) return undefined;
        setQuery("");
        const index = cities.findIndex((c) => String(c.id) === String(value));
        setHighlight(index < 0 ? 0 : index);
        // Let the panel mount before focusing so mobile keyboards open reliably
        const t = setTimeout(() => inputRef.current?.focus(), 30);
        if (isMobile) document.body.style.overflow = "hidden";
        return () => {
            clearTimeout(t);
            if (isMobile) document.body.style.overflow = "";
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        setHighlight(0);
    }, [query]);

    useEffect(() => {
        listRef.current?.children[highlight]?.scrollIntoView({ block: "nearest" });
    }, [highlight, open]);

    const choose = (city) => {
        onChange(String(city.id));
        setOpen(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, filtered.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (filtered[highlight]) choose(filtered[highlight]);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    const panel = (
        <>
            <div className="cs-search">
                <i className="fas fa-magnifying-glass"></i>
                <input
                    ref={inputRef}
                    type="text"
                    placeholder="Search city…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    autoComplete="off"
                />
                {query && (
                    <button type="button" className="cs-clear" onClick={() => setQuery("")} aria-label="Clear search">
                        <i className="fas fa-xmark"></i>
                    </button>
                )}
            </div>
            {filtered.length > 0 && (
                <ul ref={listRef} className="cs-list" role="listbox">
                    {filtered.map((c, i) => {
                        const isSelected = String(c.id) === String(value);
                        return (
                            <li
                                key={c.id}
                                role="option"
                                aria-selected={isSelected}
                                className={`${i === highlight ? "hl" : ""} ${isSelected ? "sel" : ""}`}
                                onMouseEnter={() => setHighlight(i)}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => choose(c)}
                            >
                                <i className="far fa-location-dot"></i>
                                <span className="cs-name">{c.name}</span>
                                {isSelected && <i className="fas fa-check cs-check"></i>}
                            </li>
                        );
                    })}
                </ul>
            )}
            {filtered.length === 0 && (
                <div className="cs-empty">
                    <i className="far fa-map"></i>
                    No cities found{query ? ` for “${query}”` : ""}
                </div>
            )}
        </>
    );

    return (
        <div ref={wrapperRef} className={`cs cs-${variant} ${open ? "open" : ""}`}>
            <button
                type="button"
                className="cs-trigger"
                onClick={() => setOpen((o) => !o)}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                {icon && <i className={`cs-icon ${icon}`}></i>}
                <span className={`cs-value ${selected ? "" : "ph"}`}>{selected ? selected.name : placeholder}</span>
                <i className="fas fa-chevron-down cs-caret"></i>
            </button>

            {open && !isMobile && <div className="cs-pop">{panel}</div>}

            {open && isMobile && createPortal(
                <div className="cs-backdrop" onClick={() => setOpen(false)}>
                    <div className="cs-sheet" onClick={(e) => e.stopPropagation()}>
                        <div className="cs-grip" />
                        <div className="cs-sheet-head">
                            <h3>{placeholder}</h3>
                            <button type="button" className="cs-close" onClick={() => setOpen(false)} aria-label="Close">
                                <i className="fas fa-xmark"></i>
                            </button>
                        </div>
                        {panel}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

export default CitySelect;
